<script setup lang="ts">
import { MContainerRow, MUButton } from "@xlabmedical/medic-web-framework";
import { useWindowSize } from "@vueuse/core";
import { computed } from "vue";
import RgProjectItemContextMenu from "./RgProjectItemContextMenu.vue";

type ProjectItemContextMenuItem = {
    label: string;
    icon?: string;
    command?: () => void;
    disabled?: boolean;
    checked?: boolean;
};

interface Props {
    isLargeScreen: boolean;
    allowsUserEditing: boolean;
    showUpdateBtn: boolean;
    showRevisionBtn: boolean;
    hideProjectItemButtonLabels: boolean;
    openButtonDataCy: string;
    openButtonDisabled: boolean;
    inSegmentationProcess: boolean;
    hasWeblinkOrder: boolean;
    isProjectActionDisabled: boolean;
    itemsExtra: Array<{ items: ProjectItemContextMenuItem[] }>;
}

defineProps<Props>();

const emits = defineEmits<{
    (e: "open-click"): void;
    (e: "delete-click"): void;
    (e: "share-click"): void;
    (e: "create-order-click"): void;
    (e: "update-order-click"): void;
    (e: "revision-approval-click"): void;
}>();

const { width } = useWindowSize();
const actionIconSize = computed(() => (width.value <= 768 ? "1.375rem" : "2rem"));
</script>

<template>
    <MContainerRow class="-mt-2 items-center w-full justify-evenly mb-4">
        <MUButton
            :iconSrc="'/icons/rg_visibility2.svg'"
            :class="[(showUpdateBtn || showRevisionBtn) && !isLargeScreen ? '-mt-1-5' : '']"
            :iconSrcHeight="actionIconSize"
            :iconSrcWidth="actionIconSize"
            :iconAbove="true"
            :label="hideProjectItemButtonLabels ? undefined : $t('shared.open')"
            v-tooltip="hideProjectItemButtonLabels ? $t('shared.open') : null"
            :background="false"
            style="height: 60px !important"
            :data-cy="openButtonDataCy"
            :disabled="openButtonDisabled"
            @click="emits('open-click')" />
        <MUButton
            v-if="!hasWeblinkOrder"
            :label="hideProjectItemButtonLabels ? undefined : $t('shared.delete')"
            v-tooltip="hideProjectItemButtonLabels ? $t('shared.delete') : null"
            :iconSrc="'/icons/rg_trash.svg'"
            :iconSrcHeight="actionIconSize"
            :iconSrcWidth="actionIconSize"
            :iconAbove="true"
            :background="false"
            :disabled="isProjectActionDisabled"
            style="height: 60px !important"
            @click.once="emits('delete-click')" />
        <MUButton
            v-if="!hasWeblinkOrder"
            :label="hideProjectItemButtonLabels ? undefined : $t('shared.share')"
            v-tooltip="hideProjectItemButtonLabels ? $t('shared.share') : null"
            :iconSrc="'/icons/rg_cloud_upload.svg'"
            :iconAbove="true"
            :background="false"
            :iconSrcHeight="actionIconSize"
            :iconSrcWidth="actionIconSize"
            :disabled="isProjectActionDisabled"
            style="height: 60px !important"
            @click.once="emits('share-click')" />
        <RgProjectItemContextMenu
            :hasWeblinkOrder="hasWeblinkOrder"
            :hideProjectItemButtonLabels="hideProjectItemButtonLabels"
            :isProjectActionDisabled="isProjectActionDisabled"
            :itemsExtra="itemsExtra" />
        <Divider v-if="allowsUserEditing || showRevisionBtn || !hasWeblinkOrder" layout="vertical" class="rg-vl" />
        <MUButton
            v-if="!hasWeblinkOrder"
            :iconSrc="'/icons/ik_shared_04_128_2.png'"
            :iconSrcHeight="actionIconSize"
            :iconSrcWidth="actionIconSize"
            :iconAbove="true"
            :label="hideProjectItemButtonLabels ? undefined : $t('weblink.createOrder')"
            v-tooltip="hideProjectItemButtonLabels ? $t('weblink.createOrder') : null"
            :background="false"
            :disabled="isProjectActionDisabled"
            style="height: 60px !important"
            @click.once="emits('create-order-click')" />
        <MUButton
            v-if="showUpdateBtn"
            :iconSrc="'/icons/ik_shared_04_128_2.png'"
            :iconSrcHeight="actionIconSize"
            :iconSrcWidth="actionIconSize"
            :iconAbove="true"
            :label="hideProjectItemButtonLabels ? undefined : $t('weblink.updateOrder')"
            v-tooltip="hideProjectItemButtonLabels ? $t('weblink.updateOrder') : null"
            :background="false"
            :disabled="isProjectActionDisabled"
            @click.once="emits('update-order-click')" />
        <MUButton
            v-if="showRevisionBtn"
            :class="[isLargeScreen ? '-mt-1' : 'rg-project-item-btn-mt']"
            :label="hideProjectItemButtonLabels ? undefined : $t('shared.revisionApproval')"
            v-tooltip="hideProjectItemButtonLabels ? $t('shared.revisionApproval') : null"
            :iconSrc="'/icons/rg_check_2.svg'"
            :iconSrcHeight="actionIconSize"
            :iconSrcWidth="actionIconSize"
            :iconAbove="true"
            :background="false"
            :disabled="isProjectActionDisabled"
            @click="emits('revision-approval-click')" />
    </MContainerRow>
</template>

<style scoped></style>
